import React, { useState } from 'react';
import { Navigate, useOutletContext } from 'react-router-dom';
import {
  Box, Button, CircularProgress, MenuItem, TextField, Typography,
} from '@mui/material';
import MemberEmailForm, { portalButtonSx, portalFieldSx } from '../components/MemberEmailForm';
import { sendPortalFeedback } from '../mishtameretPortalApi';
import { examsPath } from '../nav';

const MAX_LENGTH = 2000;
const KINDS = [
  { value: 'feedback', label: 'משוב' },
  { value: 'request', label: 'בקשה' },
];

const Card = ({ theme, title, lead, children }) => (
  <Box
    component="section"
    sx={{
      bgcolor: theme.surface,
      borderRadius: 2.5,
      border: `1px solid ${theme.borderSoft}`,
      p: { xs: 2, sm: 2.5 },
      mb: 2.5,
    }}
  >
    <Typography component="h2" sx={{ fontFamily: theme.fontDisplay, fontWeight: 700, fontSize: '1.15rem', color: theme.text }}>
      {title}
    </Typography>
    <Typography sx={{ color: theme.textMuted, fontSize: '0.9rem', mt: 0.5, mb: 2 }}>{lead}</Typography>
    {children}
  </Box>
);

const FeedbackForm = ({ theme, hasEmail }) => {
  const [kind, setKind] = useState('feedback');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setSent(false);
    try {
      await sendPortalFeedback({ kind, message });
      setMessage('');
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'שליחת ההודעה נכשלה. נסו שוב');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <TextField
        select
        size="small"
        label="סוג הפנייה"
        value={kind}
        onChange={(e) => setKind(e.target.value)}
        sx={{ ...portalFieldSx(theme), maxWidth: 220 }}
      >
        {KINDS.map((k) => <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>)}
      </TextField>
      <TextField
        multiline
        minRows={4}
        label="תוכן ההודעה"
        value={message}
        onChange={(e) => { setMessage(e.target.value); setSent(false); }}
        inputProps={{ maxLength: MAX_LENGTH }}
        sx={portalFieldSx(theme)}
      />
      {!hasEmail ? (
        <Typography sx={{ color: theme.textMuted, fontSize: '0.85rem' }}>
          אם תרצו מענה במייל, הוסיפו כתובת למעלה לפני השליחה.
        </Typography>
      ) : null}
      {error ? <Typography role="alert" sx={{ color: theme.danger, fontSize: '0.9rem' }}>{error}</Typography> : null}
      {sent ? <Typography role="status" sx={{ color: theme.text, fontWeight: 600 }}>ההודעה התקבלה, תודה!</Typography> : null}
      <Box>
        <Button type="submit" disabled={busy || !message.trim()} sx={portalButtonSx(theme)}>
          {busy ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : 'שליחה'}
        </Button>
      </Box>
    </Box>
  );
};

/** משוב ובקשות למערכת + כתובת המייל של הנבחן. לא זמין בעמדות קהילות */
const ContactScreen = () => {
  const { theme, member, onMemberChange, isStation = false } = useOutletContext();

  if (isStation) return <Navigate to={examsPath(undefined, true)} replace />;

  return (
    <Box>
      <Card
        theme={theme}
        title="כתובת המייל שלי"
        lead="לכתובת הזו יישלח הציון בסיום כל מבחן. מחיקת הכתובת תפסיק את המיילים."
      >
        <MemberEmailForm theme={theme} member={member} onSaved={onMemberChange} />
      </Card>
      <Card
        theme={theme}
        title="משוב או בקשה למערכת"
        lead="כתבו לנו מה עובד טוב, מה אפשר לשפר, או מה הייתם רוצים שנוסיף."
      >
        <FeedbackForm theme={theme} hasEmail={Boolean(member.email)} />
      </Card>
    </Box>
  );
};

export default ContactScreen;
